<template>
  <div class="location-selection">
    <step-header
      v-if="showHeader"
      title="Select a location"
    />
    <div class="body">
      <loading-status v-if="!locationsError && (locationsLoading || awaitingContext)" />
      <div
        v-else-if="locationsError"
        role="alert"
        data-cy="locations-error"
      >
        <pp-form-message
          type="error"
          text="Unable to load locations. Please refresh the page and try again."
        />
      </div>
      <div
        v-else
        class="selectloc location-blocks"
      >
        <div
          v-for="location in locations"
          :key="location.locationId"
          class="location-block loc-modal"
        >
          <div class="location-name">
            {{ location.name }}
          </div>
          <div class="location-map">
            <iframe
              :src="mapUrl(location)"
              loading="lazy"
              frameborder="0"
              tabindex="-1"
              allowfullscreen
              :title="`Map for ${location.name}`"
            />
          </div>
          <div class="location-address">
            {{ locationAddress(location) }}
          </div>
          <div class="loc-select-hover" />
          <div class="call-to-actions">
            <pp-button
              class="loc-select"
              type="primary"
              text="Select"
              data-cy="location-select"
              :aria-label="`Select ${location.name}`"
              @click="onSelectLocation(location)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import StepHeader from '@/components/StepHeader/StepHeader.vue'
import LoadingStatus from '@/components/LoadingStatus/LoadingStatus.vue'
import { GOOGLE_MAPS_API_KEY } from '@/constants'
import { trackView, trackClick } from '@/analytics'

export default {
  name: 'LocationSelection',
  components: { StepHeader, LoadingStatus },
  data () {
    return {
      // Locations can finish before review-context. Keep the spinner up so a
      // click cannot replace the appointment's office and provider.
      awaitingContext: true
    }
  },
  computed: {
    ...mapGetters({
      locations: 'reputation/locations',
      locationsLoading: 'reputation/locationsLoading',
      locationsError: 'reputation/locationsError',
      selectedLocation: 'reputation/selectedLocation',
      analyticsDims: 'reputation/analyticsDims'
    }),
    // A preselected or single location skips this step, so the title only
    // appears once the list (or an error) is actually going to be shown.
    showHeader () {
      return !this.awaitingContext || Boolean(this.locationsError)
    }
  },
  async created () {
    const locationsRequest = (!this.locations.length && !this.locationsError)
      ? this.loadLocations()
      : null

    // Boot starts review-context before mount. Wait for it so a review hash can
    // preselect location, provider, and patient name before this screen decides.
    await this.whenReviewContextReady()
    if (locationsRequest) await locationsRequest

    const preselectedId = this.selectedLocation && this.selectedLocation.locationId
    const preselectedLegacyId = this.selectedLocation && this.selectedLocation.legacyLocationId

    if (this.locationsError) return

    if (this.locations.length === 0) {
      await this.setLocationsError(new Error('No locations found'))
      return
    }

    // Only a boot stub (no name) should auto-forward. A full record means the
    // patient already chose, or this screen is showing the list.
    const isBootStub = this.selectedLocation && !this.selectedLocation.name
    if (isBootStub && (preselectedId || preselectedLegacyId != null)) {
      const full = preselectedId
        ? this.locations.find(l => l.locationId === preselectedId)
        : this.locations.find(l => String(l.legacyLocationId) === String(preselectedLegacyId))
      if (full) {
        // Overwrite the URL-param stub with the complete record (includes legacyPracticeId / legacyLocationId)
        await this.selectLocation({ location: full, preserveProvider: true })
        this.$router.replace({ name: 'ProviderSelection' })
        return
      }
      // Unknown UUID: fall through and show the full selection UI
    }

    if (this.locations.length === 1) {
      // The old screen records this view in initialize, before the single
      // location auto-skip, so the location id is still empty.
      trackView(this, 'location_selection', this.analyticsDims)
      await this.selectLocation({ location: this.locations[0], preserveProvider: true })
      this.$router.replace({ name: 'ProviderSelection' })
      return
    }

    this.awaitingContext = false
    // The old screen records one view for the step, then one more per tile.
    const dims = this.analyticsDims
    trackView(this, 'location_selection', dims)
    this.locations.forEach(() => trackView(this, 'location_selection', dims))
  },
  methods: {
    ...mapActions({
      loadLocations: 'reputation/loadLocations',
      selectLocation: 'reputation/selectLocation',
      setLocationsError: 'reputation/setLocationsError',
      whenReviewContextReady: 'reputation/whenReviewContextReady'
    }),
    locationAddress (location) {
      return [location.address1, location.address2, location.city, location.state, location.zip]
        .filter(Boolean)
        .join(', ')
    },
    mapUrl (location) {
      const fullAddress = encodeURIComponent(
        [location.address1, location.address2, location.city, location.state, location.zip]
          .filter(Boolean)
          .join(',')
      )
      return `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${fullAddress}`
    },
    async onSelectLocation (location) {
      trackClick(this, location.name, this.analyticsDims)
      await this.selectLocation({ location })
      return this.$router.push({ name: 'ProviderSelection' })
    }
  }
}
</script>

<style lang="scss" scoped>
.location-blocks {
  display: inline-block;
  text-align: center;
  width: 100%;
}

.loc-select-hover {
  background-color: #fcfcff;
  display: none;
  height: 176px;
  margin: -176px 0 0;
  opacity: .4;
  pointer-events: none;
  position: absolute;
  width: 250px;
}

.loc-select {
  border-radius: 0 0 5px 5px;
  font-family: $font-family-base;
  height: 37px;
  position: relative;
  width: 100%;
  z-index: 1;

  // The visible control stays the bottom bar. This extension is the rest of
  // the hit target, clipped to the card so it cannot cover the next tile.
  &:after {
    background-color: transparent;
    bottom: 0;
    content: '';
    left: 0;
    position: absolute;
    right: 0;
    top: -1000px;
  }
}

.location-block {
  background: #fff;
  border-radius: 5px;
  cursor: default;
  display: inline-block;
  height: 213px;
  margin: 8px;
  overflow: hidden;
  position: relative;
  vertical-align: top;
  width: 250px;

  &:hover {
    .loc-select-hover {
      display: block;
    }

    .loc-select {
      background: var(--color-primary-dark, #3e4760);
      border-color: var(--color-primary-dark, #3e4760);
    }
  }
}

.location-name {
  color: $text-primary;
  font-size: 13px;
  font-weight: 700;
  overflow: hidden;
  padding: 5px;
  pointer-events: none;
  text-align: left;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.location-map {
  height: 150px;
  margin: 0;
  pointer-events: none;
  width: 250px;

  iframe {
    border: 0;
    display: block;
    height: 100%;
    pointer-events: none;
    width: 100%;
  }
}

.location-address {
  background: rgba(255, 255, 255, .85);
  color: #333;
  font-size: 12px;
  left: 17px;
  padding: 5px 10px;
  pointer-events: none;
  position: absolute;
  top: 105px;
  width: 220px;
}

.call-to-actions {
  bottom: 0;
  left: 0;
  position: absolute;
  width: 100%;
  z-index: 2;
}

@include mobile {
  .location-block {
    display: block;
    height: auto;
    margin: 8px 0;
    width: 100%;
  }

  .location-map {
    height: 150px;
    width: 100%;
  }

  .location-address {
    width: calc(100% - 34px);
  }

  .loc-select-hover {
    width: 100%;
  }
}
</style>
